// frontend/src/services/apiService.js

import axios from 'axios';

const API_URL = process.env.VUE_APP_API_URL;

// Create axios instance with base configuration
const apiClient = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Function to get auth token from localStorage
const getAuthToken = () => {
    return localStorage.getItem('authToken');
};

// Function to get current user data from localStorage
const getCurrentUser = () => {
    const userStr = localStorage.getItem('currentUser');
    return userStr ? JSON.parse(userStr) : null;
};

// Function to clear auth data
const clearAuthData = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
    localStorage.clear(); // this removes all keys, including legacy ones
};


// Request interceptor to add auth token
apiClient.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle 401 errors
apiClient.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (error.response && error.response.status === 401) {
            // Clear auth data and redirect to login
            clearAuthData();
            // Check if we're not already on login page to avoid infinite redirect
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

export default {
    // Auth methods
    getAuthToken,
    getCurrentUser,
    clearAuthData,

    // Login method
    async login(username, password) {
        try {
            const formData = new URLSearchParams();
            formData.append('username', username);
            formData.append('password', password);

            const response = await axios.post(`${API_URL}/token`, formData.toString(), {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });

            // Handle different response types from backend
            if (response.data.requiresPasswordReset) {
                return {
                    success: false,
                    requiresPasswordReset: true,
                    error: 'You must reset your password before logging in'
                };
            }

            if (response.data.accountInactive) {
                return {
                    success: false,
                    accountInactive: true,
                    error: 'Your account is inactive. Please contact an administrator.'
                };
            }

            if (response.data.access_token) {
                // Store auth data using correct keys for the new system
                localStorage.setItem('authToken', response.data.access_token);

                // Store user data as JSON object
                const userData = {
                    id: response.data.userId,
                    username: username,
                    roles: response.data.roles,
                    org_id: response.data.org_id
                };
                localStorage.setItem('currentUser', JSON.stringify(userData));
                console.log("ROLES ARE ", response.data.roles);

                // Keep legacy keys for backward compatibility (if needed)
                localStorage.setItem('token', response.data.access_token);
                localStorage.setItem('roles', JSON.stringify(response.data.roles));
                localStorage.setItem('username', username);
                localStorage.setItem('userId', response.data.userId);
                localStorage.setItem('org_id', response.data.org_id);
                localStorage.setItem('loggedIn', 'true'); // For legacy compatibility

                return {
                    success: true,
                    user: userData,
                    token: response.data.access_token
                };
            } else {
                return {
                    success: false,
                    error: 'Login failed. Please try again.'
                };
            }
        } catch (error) {
            console.error('Login error:', error);
            let errorMessage = 'An error occurred while trying to log in';

            if (error.response?.status === 401) {
                errorMessage = 'Invalid username or password';
            }

            return {
                success: false,
                error: errorMessage
            };
        }
    },

    async fetchUsers() {
        // console.log('Fetching users from API...');
        const response = await apiClient.get('/users');
        return response.data.users;
    },

    async fetchClasses(userId) {
        try {
            const response = await apiClient.get('/classes', {
                params: {user_id: userId}
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching classes:', error);
            throw error;
        }
    },

    async fetchStudentsInClass(classId) {
        try {
            const response = await apiClient.get(`/classes/${classId}/students`);
            return response.data;
        } catch (error) {
            console.error('Error fetching students in class:', error);
            throw error;
        }
    },

    // Student management methods (protected endpoints)
    async fetchStudents() {
        try {
            const response = await apiClient.get('/students');
            return response.data;
        } catch (error) {
            console.error('Error fetching students:', error);
            throw error;
        }
    },

    async addStudent(studentData) {
        try {
            const response = await apiClient.post('/add-student', {
                name: studentData.name,
                email: studentData.email,
                gender: studentData.gender
            });
            return response.data;
        } catch (error) {
            console.error('Error adding student:', error);
            throw error;
        }
    },

    // Feedback methods (protected endpoints)
    async submitFeedback(feedbackData) {
        try {
            const response = await apiClient.post('/feedback', feedbackData);
            return response.data;
        } catch (error) {
            console.error('Error submitting feedback:', error);
            throw error;
        }
    },

    async fetchFeedback() {
        try {
            const response = await apiClient.get('/feedback');
            return response.data;
        } catch (error) {
            console.error('Error fetching feedback:', error);
            throw error;
        }
    },

    // Chat/Messages methods (protected endpoints)
    async fetchChatMessages(chatId) {
        try {
            const response = await apiClient.get('/messages_chat', {
                params: {chat_id: chatId}
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching chat messages:', error);
            throw error;
        }
    },

    async sendChatMessage(chatId, messageData) {
        try {
            const response = await apiClient.post(`/messages_chat?chat_id=${chatId}`, messageData);
            return response.data;
        } catch (error) {
            console.error('Error sending chat message:', error);
            throw error;
        }
    },

    async fetchMessages(userId) {
        try {
            const response = await apiClient.get('/messages', {
                params: {user_id: userId}
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching messages:', error);
            throw error;
        }
    },

    async sendMessage(messageData) {
        try {
            const response = await apiClient.post('/messages', messageData);
            return response.data;
        } catch (error) {
            console.error('Error sending message:', error);
            throw error;
        }
    },

    async replyToMessage(messageId, replyData) {
        try {
            const response = await apiClient.post(`/messages/${messageId}/reply`, replyData);
            return response.data;
        } catch (error) {
            console.error('Error replying to message:', error);
            throw error;
        }
    },

    // Class management methods (protected endpoints)
    async createClass(classData) {
        try {
            const response = await apiClient.post('/classes', classData);
            return response.data;
        } catch (error) {
            console.error('Error creating class:', error);
            throw error;
        }
    },


    async addStudentToClass(classId, studentId) {
        try {
            const response = await apiClient.post(`/classes/${classId}/students`, {
                class_id: classId,
                student_id: studentId
            });
            return response.data;
        } catch (error) {
            console.error('Error adding student to class:', error);
            throw error;
        }
    },

    async removeStudentFromClass(classId, studentId) {
        try {
            const response = await apiClient.delete(`/classes/${classId}/students/${studentId}`);
            return response.data;
        } catch (error) {
            console.error('Error removing student from class:', error);
            throw error;
        }
    },


    // User management methods (protected endpoints)
    async createUser(userData) {
        try {
            const response = await apiClient.post('/users', userData);
            return response.data;
        } catch (error) {
            console.error('Error creating user:', error);
            throw error;
        }
    },

    async updateUser(userId, userData) {
        try {
            const response = await apiClient.put(`/users/${userId}`, userData);
            return response.data;
        } catch (error) {
            console.error('Error updating user:', error);
            throw error;
        }
    },

    async deleteUser(userId) {
        try {
            const response = await apiClient.delete(`/users/${userId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting user:', error);
            throw error;
        }
    },

    async fetchRoles() {
        try {
            const response = await apiClient.get('/users/roles');
            return response.data;
        } catch (error) {
            console.error('Error fetching roles:', error);
            throw error;
        }
    },

    async fetchUserLogins() {
        try {
            const response = await apiClient.get('/users/logins');
            return response.data;
        } catch (error) {
            console.error('Error fetching user logins:', error);
            throw error;
        }
    },

    // Organization management methods (protected endpoints)
    async getOrganizations() {
        try {
            const response = await apiClient.get('/organizations');
            return response.data;
        } catch (error) {
            console.error('Error fetching organizations:', error);
            throw error;
        }
    },

    async createOrganization(orgData) {
        try {
            const response = await apiClient.post('/organizations', orgData);
            return response.data;
        } catch (error) {
            console.error('Error creating organization:', error);
            throw error;
        }
    },

    async updateOrganization(orgId, orgData) {
        try {
            const response = await apiClient.put(`/organizations/${orgId}`, orgData);
            return response.data;
        } catch (error) {
            console.error('Error updating organization:', error);
            throw error;
        }
    },

    async deleteOrganization(orgId) {
        try {
            const response = await apiClient.delete(`/organizations/${orgId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting organization:', error);
            throw error;
        }
    },

    async updateClass(classId, classData) {
        try {
            const response = await apiClient.put(`/classes/${classId}`, classData);
            return response.data;
        } catch (error) {
            console.error('Error updating class:', error);
            throw error;
        }
    },

    async deleteClass(classId) {
        try {
            const response = await apiClient.delete(`/classes/${classId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting class:', error);
            throw error;
        }
    },

    // Student management methods (protected endpoints)
    async createStudent(studentData) {
        try {
            const response = await apiClient.post('/students', studentData);
            return response.data;
        } catch (error) {
            console.error('Error creating student:', error);
            throw error;
        }
    },

    async updateStudent(studentId, studentData) {
        try {
            const response = await apiClient.put(`/students/${studentId}`, studentData);
            return response.data;
        } catch (error) {
            console.error('Error updating student:', error);
            throw error;
        }
    },

    async deleteStudent(studentId) {
        try {
            const response = await apiClient.delete(`/students/${studentId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting student:', error);
            throw error;
        }
    },

    async uploadStudentsFile(classId, file) {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await apiClient.post(`/classes/${classId}/upload-students`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            console.error('Error uploading students file:', error);
            throw error;
        }
    },


    // Password reset methods
    async getUserByUsername(username) {
        try {
            const response = await apiClient.get(`/users/by-username/${username}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching user by username:', error);
            throw error;
        }
    },

    async verifyUserPassword(username, password) {
        try {
            // Use the token endpoint to verify credentials
            const formData = new URLSearchParams();
            formData.append('username', username);
            formData.append('password', password);

            const response = await axios.post(`${API_URL}/token`, formData.toString(), {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });

            return {
                success: true,
                token: response.data.access_token,
                userId: response.data.userId
            };
        } catch (error) {
            console.error('Error verifying user password:', error);
            return {
                success: false,
                error: 'Invalid username or password'
            };
        }
    },

    async resetUserPassword(username, currentPassword, newPassword) {
        try {
            // Use the new public password reset endpoint
            const response = await axios.post(`${API_URL}/users/reset-password`, {
                username: username,
                current_password: currentPassword,
                new_password: newPassword
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('Error resetting password:', error);

            let errorMessage = 'An error occurred while resetting password';
            if (error.response?.status === 401) {
                errorMessage = 'Current password is incorrect';
            } else if (error.response?.status === 404) {
                errorMessage = 'User not found';
            }

            throw new Error(errorMessage);
        }
    },

    // Profile management methods (protected endpoints)
    async fetchUserProfile(userId) {
        try {
            const response = await apiClient.get(`/profile/${userId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching user profile:', error);
            throw error;
        }
    },

    async createUserProfile(profileData) {
        try {
            const response = await apiClient.post('/profile', profileData);
            return response.data;
        } catch (error) {
            console.error('Error creating user profile:', error);
            throw error;
        }
    },

    async updateUserProfile(userId, profileData) {
        try {
            const response = await apiClient.put(`/profile/${userId}`, profileData);
            return response.data;
        } catch (error) {
            console.error('Error updating user profile:', error);
            throw error;
        }
    },

    async deleteUserProfile(userId) {
        try {
            const response = await apiClient.delete(`/profile/${userId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting user profile:', error);
            throw error;
        }
    },

    async updateUserStatus(userId, status) {
        try {
            const response = await apiClient.put(`/users/${userId}/status`, {status});
            return response.data;
        } catch (error) {
            console.error('Error updating user status:', error);
            throw error;
        }
    },


    // Album management methods (protected endpoints)
    async getAlbums() {
        try {
            const response = await apiClient.get('/albums');
            return response.data;
        } catch (error) {
            console.error('Error getting album: ', error);
            throw error;
        }
    },

    async getAlbumById(album_id) {
        try {
            const response = await apiClient.get(`albums/${album_id}`);
            return response.data;
        } catch (error) {
            console.error('Error getting album: ', error);
            throw error;
        }
    },

    async createAlbum(album) {
        try {
            const response = await apiClient.post('/albums', album);
            return response.data;
        } catch (error) {
            console.error('Error creating album: ', error);
            throw error;
        }
    },

    // Image management methods (protected endpoints)
    async getImagesByUser() {
        try {
            const response = await apiClient.get('/images');
            return response.data
        } catch (error) {
            console.error("Error getting images: ", error);
            throw error;
        }
    },

    async getUploadUrl(newImage) {
        try {
            const response = await apiClient.post('/images/init-upload', newImage)
            return response.data
        } catch (error) {
            console.error("Error getting upload URL: ", error);
            throw error;
        }
    },

    async uploadImageToStorage(file, uploadUrl) {
        try {
            await fetch(uploadUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': file.type
                },
                body: file
            })
        } catch (error) {
            console.error('Error uploading image: ', error);
            throw error;
        }
    },

    async createImage(newImage) {
        try {
            const response = await apiClient.post('/images', newImage)
            return response.data
        } catch (error) {
            console.error("Error creating image: ", error);
            throw error;
        }
    },

    async getImageSignedUrl(image_id) {
        try {
            const response = await apiClient.get(`/images/${image_id}/signed`)
            return response.data
        } catch (error) {
            console.error("Error getting image: ", error);
            throw error;
        }
    },

    async getImage(image_id) {
        try {
            const response = await apiClient.get(`/images/${image_id}/data`);
            return response.data
        } catch (error) {
            console.error("Error getting image: ", error);
            throw error;
        }
    },

    async deleteImage(image_id) {
        try {
            const response = await apiClient.delete(`/images/${image_id}`);
            return response.data
        } catch (error) {
            console.error("Error deleting image: ", error);
            throw error;
        }
    },

    async updateImage(image_id, editImage) {
        try {
            const response = await apiClient.put(`/images/${image_id}`, editImage);
            return response.data
        } catch (error) {
            console.error("Error updating image: ", error);
            throw error;
        }
    },

    async getNumberOfLikes(image_id) {
        try {
            const response = await apiClient.get(`/images/${image_id}/like-count`);
            return response.data
        } catch (error) {
            console.error("Error getting number of likes: ", error);
            throw error;
        }
    },

    async getImageLikeStatus(image_id) {
        try {
            const response = await apiClient.get(`/images/${image_id}/check-like`);
            return response.data
        } catch (error) {
            console.error("Error getting like status: ", error);
            throw error;
        }
    },

    async addLikeToImage(image_id) {
        try {
            const response = await apiClient.get(`/images/${image_id}/add-like`);
            return response.data
        } catch (error) {
            console.error("Error adding like to image: ", error);
            throw error;
        }
    },

    async removeLikeFromImage(image_id) {
        try {
            const response = await apiClient.get(`/images/${image_id}/remove-like`);
            return response.data
        } catch (error) {
            console.error("Error removing like from image: ", error);
            throw error;
        }
    },

    async getRandomizedFeed(limit) {
        try {
            const response = await apiClient.get(`/feed?filter_by=random&limit=${limit}`);
            return response.data
        } catch (error) {
            console.error("Error getting randomized feed: ", error);
            throw error;
        }
    },

    async getFilteredFeed(filterBy, limit, skip) {
        try {
            const response =
                await apiClient.get(`/feed?filter_by=${filterBy}&limit=${limit}&skip=${skip}`);
            return response.data
        } catch (error) {
            console.error("Error getting randomized feed: ", error);
            throw error;
        }
    },

    // Contest management methods (protected endpoints)

    async createContest(contest) {
        try {
            const response = await apiClient.post('/contests', contest)
            return response.data
        } catch (error) {
            console.error("Error creating contest: ", error);
            throw error;
        }
    },

    async getCurrentContests() {
        try {
            const response = await apiClient.get('/contests/current')
            return response.data
        } catch (error) {
            console.error("Error getting contests: ", error);
            throw error;
        }
    },

    async getRecentContests() {
        try {
            const response = await apiClient.get('/contests/recent')
            return response.data
        } catch (error) {
            console.error("Error getting contests: ", error);
            throw error;
        }
    },

    async getPastContests() {
        try {
            const response = await apiClient.get('/contests/past')
            return response.data
        } catch (error) {
            console.error("Error getting contests: ", error);
            throw error;
        }
    },

    async getContestById(contest_id) {
        try {
            const response = await apiClient.get(`/contests/${contest_id}`);
            return response.data;
        } catch (error) {
            console.error("Error getting contest: ", error);
            throw error;
        }
    },

    async getImageIdsInContest(contest_id) {
        try {
            const response = await apiClient.get(`/contests/${contest_id}/images`);
            return response.data;
        } catch (error) {
            console.error("Error getting contest images: ", error);
            throw error;
        }
    },

    async getNumberOfSubmissionsPerUser(contest_id) {
        try {
            const response = await apiClient.get(`/contests/${contest_id}/submissions`);
            return response.data;
        } catch (error) {
            console.error("Error getting contest images: ", error);
            throw error;
        }
    },

    async submitImages(contest_id, images) {
        try {
            const response = await apiClient.post(`/contests/${contest_id}`, images);
            return response.data;
        } catch (error) {
            console.error("Error submitting images to contest: ", error);
            throw error;
        }
    },

    async getVotesForImageInContest(contest_id, image_id) {
        try {
            const response = await apiClient.get(`/contests/${contest_id}/${image_id}/votes`);
            return response.data;
        } catch (error) {
            console.error("Error getting votes for image: ", error);
            throw error;
        }
    },

    async getImageIdsInContestWithVotes(contest_id) {
        try {
            const response = await apiClient.get(`/contests/${contest_id}/images-with-votes`);
            return response.data;
        } catch (error) {
            console.error("Error getting contest image IDs with votes: ", error);
            throw error;
        }
    },

    async getUserVoteStatus(contest_id) {
        try {
            const response = await apiClient.get(`/contests/${contest_id}/voted`);
            return response.data;
        } catch (error) {
            console.error("Error getting user vote status: ", error);
            throw error;
        }
    },

    async submitVoteForImage(image_id, contest_id) {
        try {
            const response = await apiClient.post(`/contests/${contest_id}/${image_id}/vote`);
            return response.data;
        } catch (error) {
            console.error("Error submitting vote for image: ", error);
            throw error;
        }
    },

    async closeContest(contest_id) {
        try {
            const response = await apiClient.get(`/contests/${contest_id}/close`);
            return response.data;
        } catch (error) {
            console.error("Error closing contest: ", error);
            throw error;
        }
    },

    // Registration management methods (protected endpoints)

    async createRegistration(registration) {
        try {
            const response = await apiClient.post('/registration', registration)
            return response.data
        } catch (error) {
            console.error("Error creating registration: ", error);
            throw error;
        }
    },

    async getRegistrations() {
        try {
            const response = await apiClient.get(`/registration`);
            return response.data;
        } catch (error) {
            console.error("Error getting registration: ", error);
            throw error;
        }
    },

    async deactivateRegistration(registration_id) {
        try {
            const response = await apiClient.get(`/registration/${registration_id}/deactivate`);
            return response.data;
        } catch (error) {
            console.error("Error deactivating registration: ", error);
            throw error;
        }
    },

    async activateRegistration(registration_id) {
        try {
            const response = await apiClient.get(`/registration/${registration_id}/activate`);
            return response.data;
        } catch (error) {
            console.error("Error activating registration: ", error);
            throw error;
        }
    },

    async checkUniqueUsername(username) {
        try {
            const response = await apiClient.get(`/users/signup/check/${username}`);
            return response.data;
        } catch (error) {
            console.error("Error checking username: ", error);
            throw error;
        }
    },

    async submitSignup(signupRequest) {
        try {
            const response = await apiClient.post(`/users/signup`, signupRequest)
            return response.data;
        } catch (error) {
            console.error("Error signing up: ", error);
            throw error;
        }
    },

    async getUnresolvedModerationCases() {
        try {
            const response = await apiClient.get('/images/moderation/unresolved')
            return response.data;
        } catch (error) {
            console.error("Error getting unresolved moderation cases: ", error);
            throw error;
        }
    },
    async denyModerationCase(moderationCaseId) {
        try {
            const response = await apiClient.get(`/images/moderation/${moderationCaseId}/deny`)
            return response.data;
        } catch (error) {
            console.error("Error getting unresolved moderation cases: ", error);
            throw error;
        }
    },
    async allowModerationCase(moderationCaseId) {
        try {
            const response = await apiClient.get(`/images/moderation/${moderationCaseId}/allow`)
            return response.data;
        } catch (error) {
            console.error("Error getting unresolved moderation cases: ", error);
            throw error;
        }
    }
};
